// src/analytics/tracker.ts

/** Event type recorded in localStorage (rich but lightweight) */
export type UXEvent =
  | {
      t: "page_view";
      path: string; // e.g. "/products/123?ref=home"
      ts: number; // timestamp (ms)
      referrer?: string | null; // previous page path
      device?: "Mobile" | "Desktop";
      duration?: number; // seconds spent on the *previous* page
    }
  | {
      t: "add_to_cart" | "checkout_start" | "order_complete";
      ts: number;
      path?: string;
      productId?: string;
      referrer?: string | null;
      device?: "Mobile" | "Desktop";
    };

const KEY = "ux_events_v1";
const MAX_EVENTS = 2000;

export const getSessionId = () => {
  const k = "ux_session_id";
  let sid = localStorage.getItem(k);
  if (!sid) {
    sid = Math.random().toString(36).slice(2);
    localStorage.setItem(k, sid);
  }
  return sid;
};

function load(): UXEvent[] {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return [];
    const data = JSON.parse(raw);
    return Array.isArray(data) ? (data as UXEvent[]) : [];
  } catch {
    return [];
  }
}
function save(list: UXEvent[]) {
  try {
    localStorage.setItem(KEY, JSON.stringify(list.slice(-MAX_EVENTS)));
  } catch {
    /* ignore quota errors */
  }
}

/** Track a page view with full path+query, referrer, device, and time on previous page */
export function trackPageView(path: string) {
  const list = load();
  const now = Date.now();
  const device: "Mobile" | "Desktop" = /Mobi|Android/i.test(navigator.userAgent)
    ? "Mobile"
    : "Desktop";

  // compute time spent on the *previous* page_view
  for (let i = list.length - 1; i >= 0; i--) {
    const last = list[i];
    if (last.t === "page_view") {
      // only set duration once
      if (typeof last.duration !== "number") {
        (last as any).duration = Math.round((now - last.ts) / 1000); // seconds
      }
      break;
    }
  }

  const lastPath =
    (list.length && list[list.length - 1].t === "page_view"
      ? (list[list.length - 1] as Extract<UXEvent, { t: "page_view" }>).path
      : null) || null;

  const entry: UXEvent = {
    t: "page_view",
    path: window.location.pathname + window.location.search || path,
    ts: now,
    referrer: lastPath,
    device,
    // duration is for the previous page; this new view starts timing now
  };

  list.push(entry);
  save(list);
}

/** Generic action tracker (e.g., add_to_cart, checkout_start, order_complete) */
export function track(
  type: Extract<
    UXEvent["t"],
    "add_to_cart" | "checkout_start" | "order_complete"
  >,
  payload: Partial<UXEvent> = {}
) {
  const list = load();
  const entry: UXEvent = {
    t: type,
    ts: Date.now(),
    path: payload.path ?? window.location.pathname + window.location.search,
    productId: (payload as any).productId,
    referrer:
      (list.length && list[list.length - 1].t === "page_view"
        ? (list[list.length - 1] as Extract<UXEvent, { t: "page_view" }>).path
        : null) || null,
    device: /Mobi|Android/i.test(navigator.userAgent) ? "Mobile" : "Desktop",
  };
  list.push(entry);
  save(list);
}

/** Read all recorded UX events (used by Dashboard) */
export function readEvents(): UXEvent[] {
  return load();
}
